import { appInsight } from 'components/appInsight';
import { collectProperties } from 'components/appInsight';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/sport-disciplina/PageContent';
import { Seo } from 'components/seo';
import { mergeTagsFromLabels, parseSlugFromParams } from 'components/seo';
import { AmbienteContext } from 'context/Ambiente';
import { AppFragment } from 'enums/path-fragment';
import { getSportDisciplinaPage, SportDisciplinaPageQuery } from 'lib/datoCms/queries/getSportDisciplinaPage';
import { cleanConfig } from 'lib/datoCms/utils';
import { preloadVisualComposerData, PreloadVisualComposerType, useVisualComposerSnaphot } from 'lib/ssr';
import { GetStaticPaths, GetStaticPathsResult } from 'next';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { IS_NEXT_PRODUCTION_BUILD_PHASE } from 'utility/constant';
import { isNotEmpty, isTruthy, toSportUrlCase } from 'utility/functions';
import { SportPageProps } from '..';

interface DisciplinaPageProps extends Omit<SportPageProps, 'cmsContent'> {
  cmsContent: SportDisciplinaPageQuery;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const result: GetStaticPathsResult<{ disciplina: string }> = {
    paths: [
      { params: { disciplina: 'calcio' } },
      { params: { disciplina: 'tennis' } },
      { params: { disciplina: 'basket' } },
      { params: { disciplina: 'volley' } },
    ],
    fallback: 'blocking',
  };

  /*
  if (!skipStaticPageGeneration()) {
    await loadIndex()

    const menu = await ssrLiveNav();

    const list = loadFlat(AppFragment.Sport, 2)
      .map((x) => `${x.loc}`.replace(`/${AppFragment.Sport}/`, ''))
      .filter((x) => !isMatch(x, '^(info|marcatori)/'));

    (menu?.discipline ?? [])
      .filter((x) => isTruthy(x?.slug?.length) && !list.some((prev) => isMatch(prev, `^${x.slug}$`)))
      .forEach((x) => list.push(`${x.slug}`));

    list.forEach((name) => {
      const disciplina = encodeURIComponent(name.toUpperCase());
      result.paths.push({ params: { disciplina } });
    });
    // result.fallback = true;
    console.log(`## sport.disciplina.getStaticPaths -> ${result.paths.length} items`);
  }
*/
  return Promise.resolve(result);
};

const noBettingRedirect = (ssrData: PreloadVisualComposerType): string | undefined => {
  let noBetting = true;

  // check betting config
  for (let fieldName of ['inEvidenza', 'topGiocate', 'topAntePost']) {
    const { template, scommesse } = Reflect.get(ssrData ?? {}, fieldName) ?? {};
    if (isTruthy(template?.length) && isTruthy(scommesse?.avvenimentoList?.length)) {
      // betting found
      noBetting = false;
      break;
    }
  }

  if (noBetting) {
    // get first manifestazione from menu
    const { menuManifestazioni } = ssrData ?? {};

    const [manifestazione] =
      (menuManifestazioni?.gruppi ?? [])
        .sort((a, b) => a.priorita - b.priorita)
        .find((x) => !isTruthy(x.isCustom) && isTruthy(x.manifestazioni?.length))?.manifestazioni ?? [];

    if (manifestazione) {
      return toSportUrlCase(`/${manifestazione.slug}`);
    }
  }

  return undefined;
};

export const getStaticProps = async ({ locale, params }) => {
  const { disciplina } = params ?? {};
  const slug = parseSlugFromParams(`${AppFragment.Sport}/${disciplina}`);
  console.log(`## sport.disciplina.getStaticProps ${slug}`);

  try {
    const prCmsData = getDatoCMSData('common', locale);
    const prPageData = getSportDisciplinaPage(slug, locale);

    const [cmsData, pageData] = await Promise.all([prCmsData, prPageData]);

    const { site, header, footer, needHelp, allLabels: labels, sidebarInstantGame, config } = cmsData ?? {};

    const prSSR = preloadVisualComposerData({
      slug,
      locale,
      visualComposer: pageData?.sportDisciplinaPage?.visualComposer ?? [],
      isDisciplina: true, // load template disciplina ???
    });

    const [ssrData] = await Promise.all([prSSR]);

    if (!IS_NEXT_PRODUCTION_BUILD_PHASE) {
      const redirectTo = noBettingRedirect(ssrData);
      if (isNotEmpty(redirectTo)) {
        console.log(`## sport.disciplina.${disciplina}.redirectTo -> "${redirectTo}"`);

        return Promise.resolve({
          redirect: {
            permanent: false,
            destination: redirectTo!,
          },
          revalidate: 10,
        });
      }
    }

    const { tabsWithAccordionModel, allMightLikes, ...pageContent } = pageData ?? {};
    const cmsContent = mergeTagsFromLabels({
      slug,
      labels,
      cmsData: pageContent,
      pageType: 'disciplina',
      fieldName: 'sportDisciplinaPage',
    });

    return Promise.resolve({
      props: {
        slug,
        site,
        footer,
        labels,
        header,
        config: cleanConfig(config),
        seoData: tabsWithAccordionModel ?? null,
        needHelp,
        mightLikes: allMightLikes ?? null,
        cmsContent,
        sidebarInstantGame,
        ...ssrData,
      },
      revalidate: 60,
    });
  } catch (exception) {
    appInsight?.trackException({
      exception,
      id: `/${slug}/index`,
      properties: collectProperties(true),
    });

    return Promise.resolve({ notFound: true });
  }
};

const Page = ({
  slug: _,
  site,
  header,
  footer,
  seoData,
  needHelp,
  mightLikes,
  cmsContent,
  sidebarInstantGame,
  ...props
}: DisciplinaPageProps) => {
  useVisualComposerSnaphot(props);
  const { seoMetaTags, canonical } = cmsContent?.sportDisciplinaPage ?? {};

  return (
    <Layout
      header={header}
      footer={footer}
      needHelp={needHelp}
      mightLikes={mightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <AmbienteContext.Provider value={{ pathScommessa: ['sport', 'scommessa'], ambiente: 'sport' }}>
        <PageContent {...cmsContent} seoData={seoData} />
      </AmbienteContext.Provider>
    </Layout>
  );
};

export default Page;
